// src/DashboardStatus.js

import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import useReservasStore from './store/reservasStore';

// Componentes do Material-UI
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material';

// Imports para o seletor de data
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Ícones
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Livres
import EventBusyIcon from '@mui/icons-material/EventBusy'; // Com Reservas

const DashboardStatus = () => {
  dayjs.locale('pt-br');
  const { chales } = useReservasStore();
  const [loading, setLoading] = useState(false);
  
  // --- ALTERAÇÃO: Estados para o intervalo de datas ---
  const [dataInicio, setDataInicio] = useState(dayjs());
  const [dataFim, setDataFim] = useState(dayjs().add(7, 'day'));
  const [statusApresentado, setStatusApresentado] = useState(null);

  const handleVerificarStatus = async () => {
    setLoading(true);

    const dataInicioFormatada = dataInicio.format('YYYY-MM-DD');
    const dataFimFormatada = dataFim.format('YYYY-MM-DD');

    // 1. Buscar todas as reservas que SOBREPÕEM o período selecionado
    const reservasRef = collection(db, 'reservas');
    const q = query(
      reservasRef,
      where('dataCheckout', '>=', dataInicioFormatada),
      where('statusReserva', 'in', ['Confirmada', 'Hospedado', 'Pago Total'])
    );
    const querySnapshot = await getDocs(q);
    const reservasDoPeriodo = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(reserva => reserva.dataCheckin <= dataFimFormatada);

    // 2. Separar os chalés entre "Livres" e "Com Reservas"
    const chaletsLivres = [];
    const chaletsComReservas = [];

    chales.forEach(chale => {
      // Encontra se existe ALGUMA reserva para este chalé na lista de reservas do período
      const reservasParaEsteChale = reservasDoPeriodo.filter(r => r.chaletId === chale.id);

      if (reservasParaEsteChale.length === 0) {
        chaletsLivres.push(chale);
      } else {
        chaletsComReservas.push({
          ...chale,
          reservas: reservasParaEsteChale // Anexa as reservas encontradas para exibição
        });
      }
    });
    
    // Ordena as listas por nome/número do chalé
    chaletsLivres.sort((a, b) => a.nome.localeCompare(b.nome));
    chaletsComReservas.sort((a, b) => a.nome.localeCompare(b.nome));

    setStatusApresentado({
      livres: chaletsLivres,
      comReservas: chaletsComReservas,
      periodo: `de ${dataInicio.format('DD/MM/YYYY')} a ${dataFim.format('DD/MM/YYYY')}`
    });

    setLoading(false);
  };
  
  // Roda a verificação uma vez ao carregar o componente
  useEffect(() => {
    if (chales.length > 0) {
        handleVerificarStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chales]);


  const StatusCard = ({ titulo, icone, cor, lista, children }) => (
    <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icone}
        <Typography variant="h6" component="h2" sx={{ ml: 1, color: cor }}>
          {titulo} ({lista.length})
        </Typography>
      </Box>
      <List dense sx={{maxHeight: 400, overflow: 'auto'}}>
        {lista.length > 0 ? children : <ListItem><ListItemText secondary="Nenhum chalé nesta categoria." /></ListItem>}
      </List>
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h5" component="h1" fontWeight="600" sx={{ mb: 2 }}>
                    Verificar Disponibilidade de Chalés
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <DatePicker
                        label="Data de Início"
                        value={dataInicio}
                        onChange={(novaData) => setDataInicio(novaData)}
                    />
                    <DatePicker
                        label="Data de Fim"
                        value={dataFim}
                        onChange={(novaData) => setDataFim(novaData)}
                    />
                    <Button
                        variant="contained"
                        onClick={handleVerificarStatus}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verificar Status'}
                    </Button>
                </Box>
            </Paper>

            {loading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Analisando status dos chalés...</Typography>
                </Box>
            ) : statusApresentado && (
                <>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        Status para o período {statusApresentado.periodo}
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <StatusCard 
                                titulo="Livres (Todo o Período)" 
                                icone={<CheckCircleIcon color="success" />} 
                                cor="success.main" 
                                lista={statusApresentado.livres}
                            >
                                {statusApresentado.livres.map(chale => (
                                    <ListItem key={chale.id}><ListItemText primary={chale.nome} /></ListItem>
                                ))}
                            </StatusCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                           <StatusCard 
                                titulo="Com Reservas no Período" 
                                icone={<EventBusyIcon color="error" />} 
                                cor="error.main" 
                                lista={statusApresentado.comReservas}
                           >
                                {statusApresentado.comReservas.map(chale => (
                                    <ListItem key={chale.id} sx={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <ListItemText primary={<strong>{chale.nome}</strong>} sx={{mb: 0}}/>
                                        {chale.reservas.map(res => (
                                            <Typography key={res.id} variant="body2" sx={{pl: 2}}>
                                                - {res.hospedeNome} ({dayjs(res.dataCheckin).format('DD/MM')} a {dayjs(res.dataCheckout).format('DD/MM')})
                                            </Typography>
                                        ))}
                                    </ListItem>
                                ))}
                           </StatusCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    </LocalizationProvider>
  );
};

export default DashboardStatus;